import { CheckCircle, Clock, RefreshCw, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import type { WalkInRequest } from '@/types';

interface WalkInApprovalStatusProps {
  walkIn: WalkInRequest;
  onAllowEntry: () => void;
  onSendAway: () => void;
  onRetry: () => void;
  onCancel?: () => void;
  isCancelling?: boolean;
  isConfirming?: boolean;
}

/**
 * Walk-in approval status display — shows awaiting, approved, denied, or timed-out states.
 * Requirements: 4.3, 4.4, 4.5, 4.6
 */
export function WalkInApprovalStatus({
  walkIn,
  onAllowEntry,
  onSendAway,
  onRetry,
  onCancel,
  isCancelling,
  isConfirming,
}: WalkInApprovalStatusProps) {
  const { t } = useTranslation();

  if (walkIn.status === 'approved') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-green)] bg-[var(--status-green-tint)] p-6 text-center">
        <CheckCircle className="mx-auto size-12 text-[var(--status-green-text)]" />
        <h2 className="text-lg font-semibold text-[var(--status-green-text)]">
          {t('guard.walkIn.approved')}
        </h2>
        <div className="space-y-1 text-sm text-[var(--text-primary)]">
          <p className="font-medium">{walkIn.visitorName}</p>
          <p className="text-[var(--text-secondary)]">{walkIn.unitBeingVisited}</p>
        </div>
        <Button
          className="min-h-[44px] w-full bg-[var(--status-green)] hover:bg-[var(--status-green)]/90"
          onClick={onAllowEntry}
          disabled={isConfirming}
        >
          {isConfirming && <RefreshCw className="mr-2 size-4 animate-spin" />}
          {t('guard.walkIn.allowEntry')}
        </Button>
      </div>
    );
  }

  if (walkIn.status === 'denied') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-red)] bg-[var(--status-red-tint)] p-6 text-center">
        <XCircle className="mx-auto size-12 text-[var(--status-red-text)]" />
        <h2 className="text-lg font-semibold text-[var(--status-red-text)]">
          {t('guard.walkIn.denied')}
        </h2>
        <div className="space-y-1 text-sm text-[var(--text-primary)]">
          <p className="font-medium">{walkIn.visitorName}</p>
          <p className="text-[var(--text-secondary)]">{walkIn.unitBeingVisited}</p>
        </div>
        <Button variant="outline" className="min-h-[44px] w-full" onClick={onSendAway}>
          {t('guard.walkIn.sendAway')}
        </Button>
      </div>
    );
  }

  if (walkIn.status === 'expired') {
    return (
      <div className="space-y-4 rounded-[var(--radius-lg)] border-2 border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-6 text-center">
        <Clock className="mx-auto size-12 text-[var(--status-amber-text)]" />
        <h2 className="text-lg font-semibold text-[var(--status-amber-text)]">
          {t('guard.walkIn.noResponse')}
        </h2>
        <div className="space-y-1 text-sm text-[var(--text-primary)]">
          <p className="font-medium">{walkIn.visitorName}</p>
          <p className="text-[var(--text-secondary)]">{walkIn.unitBeingVisited}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="min-h-[44px] flex-1" onClick={onSendAway}>
            {t('guard.walkIn.sendAway')}
          </Button>
          <Button className="min-h-[44px] flex-1" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  // Pending / awaiting approval
  return (
    <div className="space-y-4 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-6 text-center">
      <RefreshCw className="mx-auto size-10 animate-spin text-[var(--text-accent)]" />
      <h2 className="text-lg font-semibold text-[var(--text-primary)]">
        {t('guard.walkIn.awaitingApproval')}
      </h2>
      <div className="space-y-1 text-sm">
        <p className="font-medium text-[var(--text-primary)]">{walkIn.visitorName}</p>
        <p className="text-[var(--text-secondary)]">{walkIn.unitBeingVisited}</p>
      </div>
      <CountdownTimer expiresAt={walkIn.expiresAt} />
      <div className="flex gap-3">
        <Button
          variant="destructive"
          className="min-h-[44px] flex-1"
          onClick={onCancel}
          disabled={Boolean(isCancelling) || !onCancel}
        >
          {isCancelling ? 'Cancelling…' : 'Cancel Request'}
        </Button>
        <Button variant="outline" className="min-h-[44px] flex-1" onClick={onSendAway}>
          {t('guard.walkIn.sendAway')}
        </Button>
      </div>
    </div>
  );
}

// ─── Countdown Timer ─────────────────────────────────────────────────────────

function CountdownTimer({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => calcRemaining(expiresAt));

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(calcRemaining(expiresAt));
    }, 1_000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (remaining <= 0) return null;

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return (
    <p className="font-mono text-2xl font-semibold text-[var(--text-primary)] tabular-nums">
      {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      <span className="ml-2 text-xs font-normal text-[var(--text-secondary)]">
        {t('guard.walkIn.remaining')}
      </span>
    </p>
  );
}

function calcRemaining(expiresAt: string): number {
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1_000));
}
